import { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

async function getCroppedBlob(imageSrc, cropPixels) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  canvas.width = 400;
  canvas.height = 400;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height, 0, 0, 400, 400);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
}

export default function PhotoUploadDialog({ open, onOpenChange, pixelIndex, onDone }) {
  const { t } = useLanguage();
  const fileRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPixels, setCropPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, pixels) => setCropPixels(pixels), []);

  const reset = () => {
    setImgSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCropPixels(null);
    setBusy(false);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImgSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!imgSrc || !cropPixels || pixelIndex == null) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imgSrc, cropPixels);
      const form = new FormData();
      form.append("file", blob, "face.jpg");
      await api.post(`/pixels/${pixelIndex}/photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      reset();
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="bg-[#FFFFFF] border-border rounded-none sm:max-w-lg" data-testid="photo-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">{t("upload.title")}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground leading-relaxed" data-testid="upload-specs">
          {t("upload.specs")}
        </p>

        {!imgSrc ? (
          <button
            onClick={() => fileRef.current?.click()}
            data-testid="upload-choose-file-btn"
            className="mt-4 w-full border border-dashed border-gold/50 hover:border-gold hover:bg-[#7D8C77]/5 transition-colors duration-200 py-16 text-xs tracking-[0.3em] text-golddark"
          >
            {t("upload.choose").toUpperCase()}
          </button>
        ) : (
          <>
            <div className="relative mt-4 h-72 bg-black border border-border" data-testid="crop-area">
              <Cropper
                image={imgSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="mt-4 flex items-center gap-4">
              <span className="text-[10px] tracking-[0.3em] text-muted-foreground">{t("upload.zoom").toUpperCase()}</span>
              <Slider
                value={[zoom]}
                min={1}
                max={3}
                step={0.05}
                onValueChange={([v]) => setZoom(v)}
                data-testid="crop-zoom-slider"
                className="flex-1"
              />
            </div>
            <div className="mt-6 flex gap-3">
              <Button
                onClick={handleSave}
                disabled={busy}
                data-testid="upload-save-btn"
                className="flex-1 rounded-none bg-[#7D8C77] text-[#FAF8F5] hover:bg-[#3C3735] text-xs tracking-[0.2em] h-11"
              >
                {busy ? t("common.loading").toUpperCase() : t("upload.save").toUpperCase()}
              </Button>
              <Button
                variant="outline"
                onClick={reset}
                data-testid="upload-cancel-btn"
                className="rounded-none border-border text-xs tracking-[0.2em] h-11"
              >
                {t("upload.cancel").toUpperCase()}
              </Button>
            </div>
          </>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="upload-file-input" />
      </DialogContent>
    </Dialog>
  );
}
